import './index.css';
import { Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useVariablesContext } from '../../stores/variables-context';
import { useGroupsContext } from '../../stores/groups-context';
import { useRulesContext } from '../../stores/rules-context';
import { useJobsContext } from '../../stores/jobs-context';
import { useDataCertificationsContext } from '../../stores/data-certifications-context';
import useGroupsLoading from '../../hooks/useGroupsLoading';
import useJobsLoading from '../../hooks/useJobsLoading';
import useRulesLoading from '../../hooks/useRulesLoading';
import useVariablesLoading from '../../hooks/useVariablesLoading';
import useDataCertificationsLoading from '../../hooks/useDataCertificationsLoading';
import useOperationAuthorizationChecks from '../../hooks/useOperationAuthorizationChecks';
import { getLinkToCreateGroup, ADMIN_JOB_CREATE, getLinkToCreateRule, getLinkToCreateVariable } from '../../navigator/links';
import { Identifier } from '../../types/identifier';
import { SideBarMenuOptions } from '../../types/side-bar-menu-options';
import { UserOperations } from '../../types/user';
import SideExplorerEntry from './side-explorer-entry';
import FilterInput from '../../components/FilterInput';

type SideExplorerProps = {
  tab: SideBarMenuOptions;
  filter: string;
  onFilter: React.Dispatch<React.SetStateAction<string>>;
};

const SideExplorer: React.FC<SideExplorerProps> = ({ tab, filter, onFilter }) => {
  const jobsContext = useJobsContext();
  const rulesContext = useRulesContext();
  const variablesContext = useVariablesContext();
  const dataCertificationsContext = useDataCertificationsContext();
  const groupsContext = useGroupsContext();
  useJobsLoading();
  useVariablesLoading();
  useDataCertificationsLoading();
  useRulesLoading();
  useGroupsLoading();

  const canCreateJob = useOperationAuthorizationChecks([UserOperations.JOB_CREATE]);
  const canCreateRule = useOperationAuthorizationChecks([UserOperations.RULE_CREATE]);
  const canCreateVariable = useOperationAuthorizationChecks([UserOperations.VARIABLE_CREATE]);
  const canCreateGroup = useOperationAuthorizationChecks([UserOperations.GROUP_CREATE]);

  function getEntries(identifiers: Array<Identifier>) {
    return identifiers.map((entry) => <SideExplorerEntry key={`${tab}-${entry.Id || entry.Name}`} id={entry.Id || entry.Name} text={entry.Name} type={tab} />);
  }

  function renderEntries() {
    switch (tab) {
      case 'Jobs':
        return getEntries(jobsContext.data);
      case 'Rules':
        return getEntries(rulesContext.data);
      case 'Variables':
        return getEntries(variablesContext.data);
      case 'Groups':
        return getEntries(groupsContext.data);
      default:
        return undefined;
    }
  }

  function filterEntries(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    onFilter(value);
    switch (tab) {
      case 'Jobs':
        if (jobsContext.filterData) jobsContext.filterData(value);
        return;
      case 'Rules':
        if (rulesContext.filterData) rulesContext.filterData(value);
        return;
      case 'Groups':
        if (groupsContext.filterData) groupsContext.filterData(value);
        return;
      case 'Variables':
        if (variablesContext.filterData) variablesContext.filterData(value);
        return;
      case 'Data Certifications':
        if (dataCertificationsContext.filterData) dataCertificationsContext.filterData(value);
        return;
      default:
        return;
    }
  }

  function getCreateLink(): string {
    switch (tab) {
      case 'Jobs':
        return ADMIN_JOB_CREATE;
      case 'Rules':
        return getLinkToCreateRule();
      case 'Variables':
        return getLinkToCreateVariable();
      case 'Groups':
        return getLinkToCreateGroup();
      default:
        return '';
    }
  }

  function isCreateButtonDisabled(): boolean {
    switch (tab) {
      case 'Jobs':
        return !canCreateJob;
      case 'Rules':
        return !canCreateRule;
      case 'Variables':
        return !canCreateVariable;
      case 'Groups':
        return !canCreateGroup;
      default:
        return true;
    }
  }

  return (
    <div className='side-explorer_root'>
      <div className='side-explorer_title-flex'>
        <div className='side-explorer_title-container'>
          <div className='side-explorer_title'>{tab}</div>
          <Tooltip title='Create'>
            <IconButton color='primary' disabled={isCreateButtonDisabled()} size='small' component={RouterLink} to={getCreateLink()}>
              <AddBoxIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <div className='side-explorer_content-flex'>
        <div className='side-explorer_content'>
          <FilterInput label={`Filter ${tab}`} value={filter} onChange={filterEntries} />
          <div className='side-explorer_entries'>{renderEntries()}</div>
        </div>
      </div>
    </div>
  );
};

export default SideExplorer;
